import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";

import { isLocale, translations } from "./translations.js";
import type { Locale, TranslationKey } from "./translations.js";

export const localeStorageKey = "safesphere.locale";

interface I18nContextValue {
  readonly locale: Locale;
  readonly selectLocale: (locale: Locale) => void;
  readonly t: (key: TranslationKey) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function suggestedLocale(): Locale {
  const storedLocale = window.localStorage.getItem(localeStorageKey);

  if (storedLocale !== null && isLocale(storedLocale)) {
    return storedLocale;
  }

  const deviceLanguages =
    navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  return deviceLanguages.some((language) => language.toLowerCase().startsWith("en")) ? "en" : "th";
}

export function I18nProvider({ children }: PropsWithChildren) {
  const [locale, setLocale] = useState<Locale>(suggestedLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      selectLocale: (nextLocale) => {
        window.localStorage.setItem(localeStorageKey, nextLocale);
        setLocale(nextLocale);
      },
      t: (key) => translations[locale][key],
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);

  if (context === null) {
    throw new Error("useI18n must be used inside I18nProvider.");
  }

  return context;
}
